'use client'

import { toast } from '@/hooks/use-toast'
import { GTMAddToWishlistHandler, GTMRemoveFromWishlistHandler } from '@/lib/analytics'
import useCommonStore from '@/store/commonStore'
import useWishListStore, { IWishList } from '@/store/wishListStore'
import StarIcon from '@heroicons/react/24/solid/StarIcon'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useState, useRef, useEffect } from 'react'


interface IObj {
  [key: string]: any
}
export default function ProductImageCarousel({ images, disc, details }: { images: string[], disc: number, details?: any }) {
  const [current, setCurrent] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const dragStartX = useRef<number | null>(null)
  const dragDelta = useRef<number>(0)
  const { wishList, updateWishList } = useWishListStore()
  const { isAuth } = useCommonStore()
  const searchParams = useSearchParams();
  const [isInWishList, setIsInWishlist] = useState(false)
  let variant_id = searchParams.get("variant_id") || "";
  const addToWishList = async (doc: IObj) => {
    const reqObj: IWishList = {
      pdt_id: doc.pdt_id,
      variant_id: variant_id || "",
      flag: !isInWishList // toggle flag
    }
    if (isInWishList) {
      GTMRemoveFromWishlistHandler({
        items: [
          {
            item_id: doc.pdt_id,
            item_variant: doc?.variant_id || "",
            price: doc?.spl_prc_in_num,
            item_name: doc?.pdt_nm,
            item_brand: doc?.brnd_nm || ""

          }
        ]
      })
    }
    else {
      GTMAddToWishlistHandler({
        items: [
          {
            item_id: doc.pdt_id,
            item_variant: doc?.variant_id || "",
            price: doc?.spl_prc_in_num || "",
            item_name: doc?.pdt_nm,
            item_brand: doc?.brnd_nm || ""

          }
        ]
      })
    }
    const result = await updateWishList(reqObj, isAuth)
    toast({
      variant: result.status ? "default" : "destructive",
      title: result.status ? "Success" : "Error",
      description: result.status ? result.message : result.error,
    })
  };

  useEffect(() => {
    setIsInWishlist(wishList.some(item => item.pdt_id === details?.pdt_id))
  }, [wishList, details?.pdt_id])
  // Auto-scroll
  useEffect(() => {
    if (images.length <= 1) return; // Don't auto-scroll if there's only one image

    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length)
    }, 3500)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [images.length])

  // Drag/swipe support
  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    dragStartX.current = 'touches' in e ? e.touches[0].clientX : e.clientX
    dragDelta.current = 0
  }
  const handleDragMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (dragStartX.current !== null) {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      dragDelta.current = clientX - dragStartX.current
    }
  }
  const handleDragEnd = () => {
    if (dragDelta.current > 50) setCurrent((prev) => (prev - 1 + images.length) % images.length)
    else if (dragDelta.current < -50) setCurrent((prev) => (prev + 1) % images.length)
    dragStartX.current = null
    dragDelta.current = 0
  }

  return (
    <div
      className="sticky top-20 w-full rounded-xl border border-chart-2 p-6 overflow-hidden bg-chart-1 h-fit"
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
      onMouseDown={handleDragStart}
      onMouseMove={e => dragStartX.current !== null && handleDragMove(e)}
      onMouseUp={handleDragEnd}
      onMouseLeave={() => dragStartX.current !== null && handleDragEnd()}
    >
      {details?.tags && details?.tags?.map((tag: any, idx: number) => (
        <div key={idx} className='absolute left-0 top-5 flex flex-col gap-1'>
          <span style={{ backgroundColor: tag?.bg_clr || "#000", color: tag?.txt_clr || "#fff" }} key={idx} className={`text-xs font-semibold px-1 py-[2px] rounded`}>{tag?.tag_labl}</span>
        </div>
      ))}
      {/* <span className="absolute top-0 left-0 bg-[#01DB80] text-white text-xs font-bold px-2 py-1 rounded-tl-lg rounded-br-lg z-[1]">{disc} <br/> OFF</span> */}
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToWishList(details) }}
        className="absolute right-10 top-4 text-[#6D401A] hover:text-red-600 z-2">
        <img src={`${process.env.NEXT_PUBLIC_ICON_URL}${isInWishList ? '17bd4ce5-874b-43ae-a87a-8a31f155362a.svg' : '9b871b36-dcdc-4f70-a107-7e9199484a38.svg'}`} alt="favourite" className='w-6 h-6' />
      </button>
      {/* <button className="absolute right-2 top-4 text-[#6D401A] hover:text-red-600 z-2"><Share2 className='w-5 h-5' /></button> */}
      {images[current] &&
        <Image
          src={`${images[current]}?w=250&h=250`}
          alt="Product"
          width={250}
          height={250}
          sizes="(max-width: 768px) 150px, 250px"
          className="object-contain w-full h-[150px] md:h-[250px]"
          loading="eager"
          fetchPriority="high"
          draggable={false}
        />
        
      }
      <div className="flex gap-3 items-center bg-[#EFE8E0] p-2 w-fit mx-auto rounded-md justify-center mt-4">
        {images.map((img, idx) => (
          <button
            key={idx}
            className={`border rounded-md p-[3px] ${current === idx ? 'border-primary bg-[#6D401A]' : 'border-gray-200 bg-[#D0C5B7]'}`}
            onClick={() => setCurrent(idx)}
            aria-label={`Go to image ${idx + 1}`}
            type="button"
          >
            <Image src={`${img}?h=100`} alt="" width={48} height={48} className="object-contain w-12 h-12 hidden md:block" />
          </button>
        ))}
      </div>
      {details?.rating > 0 && <div className='absolute bottom-3 right-0 flex justify-between gap-1 bg-[linear-gradient(90deg,_#FFF8BF_0%,_#FFFFFF_96.49%)] rounded-[6px] p-1 flex items-center gap-1 text-xs text-black'>
        <StarIcon className="w-3 h-3 text-yellow-400" />
        <p className="font-bold text-[#E3A600]">{details?.rating > 0 ? details?.rating : "No reviews"}</p>
        <span className="text-xs text-gray-500 font-medium">
          ({details?.total_ratings})
        </span>
      </div>}
    </div>
  )
}